import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useToast,
  Spinner,
  Text,
  Flex,
  Image,
  Badge,
} from '@chakra-ui/react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { auth } from '../../utils/firebaseConfig';

interface Post {
  id: string;
  userId: string;
  content?: string;
  timestamp?: any;
  media?: Array<{ url: string; type: string }>;
  taggedUsers?: Array<{ id: string; firstName: string; lastName: string }>;
}

function PostsManagement() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const fetchPosts = async () => {
    const authToken = await auth.currentUser?.getIdToken();
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}admin/posts`,
      {
        headers: {
          Authorization: authToken,
          Accept: 'application/json',
        },
      }
    );
    return response.data;
  };

  const { data: posts, isLoading, error } = useQuery<Post[]>({
    queryKey: ['admin-posts'],
    queryFn: fetchPosts,
  });

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      const authToken = await auth.currentUser?.getIdToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}admin/posts/${postId}`,
        {
          headers: {
            Authorization: authToken,
            'Content-Type': 'application/json',
          },
        }
      );

      toast({
        title: 'Post deleted',
        description: 'Post has been deleted successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      queryClient.invalidateQueries({ queryKey: ['admin-posts'] });
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete post.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Text color="red.500">Error loading posts. Please try again.</Text>
      </Box>
    );
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" colorScheme="gray">
        <Thead>
          <Tr>
            <Th>Post ID</Th>
            <Th>Content</Th>
            <Th>Media</Th>
            <Th>Tagged Users</Th>
            <Th>Created</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {posts && posts.length > 0 ? (
            posts.map((post) => (
              <Tr key={post.id}>
                <Td>
                  <Text fontSize="xs" fontFamily="mono">
                    {post.id.substring(0, 8)}...
                  </Text>
                </Td>
                <Td maxW="300px">
                  <Text noOfLines={3}>{post.content || 'No content'}</Text>
                </Td>
                <Td>
                  {post.media && post.media.length > 0 ? (
                    <Flex gap={2} flexWrap="wrap">
                      {post.media.slice(0, 2).map((item, idx) => (
                        <Image
                          key={idx}
                          src={item.url}
                          alt={`Media ${idx + 1}`}
                          boxSize="50px"
                          objectFit="cover"
                          borderRadius="md"
                        />
                      ))}
                      {post.media.length > 2 && (
                        <Badge>+{post.media.length - 2}</Badge>
                      )}
                    </Flex>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      No media
                    </Text>
                  )}
                </Td>
                <Td>
                  {post.taggedUsers && post.taggedUsers.length > 0 ? (
                    <Badge colorScheme="blue">
                      {post.taggedUsers.length} user(s)
                    </Badge>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      None
                    </Text>
                  )}
                </Td>
                <Td>
                  <Text fontSize="sm">{formatDate(post.timestamp)}</Text>
                </Td>
                <Td>
                  <Button
                    colorScheme="red"
                    size="sm"
                    onClick={() => handleDeletePost(post.id)}
                  >
                    Delete
                  </Button>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={6} textAlign="center">
                <Text>No posts found</Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
}

export default PostsManagement;

